import { useState } from "react";
import { ProviderFilter } from "./ProviderFilter";
import { cn } from "utils/styles";
import { DateFilter } from "./DateFilter";
import useAuth from "hooks/useAuth";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

type PageFilterProps = {
  hideTimeFilter?: boolean;
  hideAllFilters?: boolean;
  children?: React.ReactElement | React.ReactElement[];
  extraButtons?: React.ReactElement | React.ReactElement[];
};

const languages = [
  { label: "Português", value: "pt" },
  { label: "Espanõl", value: "es" },
];

export const PageFilter = ({
  children,
  hideTimeFilter = false,
  hideAllFilters = false,
  extraButtons,
}: PageFilterProps) => {
  const [showFilter, setShowFilter] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const handleToggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleToggleLanguageSelector = () => {
    setShowLanguageSelector(!showLanguageSelector);
  };

  const handleChangeLanguage = (value: string) => () => {
    i18n.changeLanguage(value);
    setShowLanguageSelector(false);
  };

  if (user?.role !== "admin" && hideTimeFilter) return null;

  return (
    <div className="flex items-center gap-4">
      {extraButtons}
      <button
        onClick={handleToggleLanguageSelector}
        className="h-9 flex items-center gap-2"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_5_107)">
            <path
              d="M20.4806 3.51934C18.2537 1.29239 15.3051 0.0499688 12.1623 0.00843752C12.1075 0.00346876 12.0532 0 11.9999 0C11.9465 0 11.8922 0.00346876 11.8374 0.00843752C8.69465 0.049922 5.74606 1.29235 3.51912 3.51929C1.25388 5.78462 0.00634766 8.79644 0.00634766 12C0.00634766 15.2035 1.25388 18.2154 3.51917 20.4807C5.74611 22.7076 8.69465 23.95 11.8375 23.9915C11.8922 23.9965 11.9466 24 11.9999 24C12.0532 24 12.1075 23.9965 12.1623 23.9915C15.3051 23.95 18.2537 22.7076 20.4806 20.4807C22.7458 18.2154 23.9934 15.2036 23.9934 12C23.9934 8.79644 22.7459 5.78462 20.4806 3.51934ZM4.52736 4.52752C5.80147 3.25341 7.34389 2.3454 9.02699 1.8548C8.64763 2.29154 8.27886 2.79385 7.93565 3.35049C7.49788 4.06046 7.11885 4.82996 6.80146 5.64357H3.55606C3.85137 5.25296 4.17509 4.87979 4.52736 4.52752ZM2.64767 7.06937H6.32141C5.93938 8.40635 5.71344 9.83069 5.65761 11.2871H1.45633C1.55501 9.79628 1.9623 8.36556 2.64767 7.06937ZM2.64771 16.9306C1.96226 15.6344 1.55496 14.2037 1.45629 12.7129H5.65756C5.71339 14.1694 5.93933 15.5937 6.32136 16.9306H2.64771ZM4.5274 19.4725C4.17504 19.1202 3.85132 18.7471 3.55606 18.3564H6.8015C7.11889 19.17 7.49788 19.9395 7.93569 20.6495C8.27891 21.2062 8.64768 21.7085 9.02704 22.1452C7.34399 21.6546 5.80156 20.7466 4.5274 19.4725ZM11.2869 22.2808C10.693 21.8922 9.89545 21.1114 9.14919 19.9012C8.8499 19.4159 8.58088 18.8989 8.34308 18.3565H11.2869L11.2869 22.2808ZM11.2869 16.9306H7.80904C7.39241 15.6096 7.14514 14.1814 7.08439 12.7129H11.2869V16.9306ZM11.2869 11.2871H7.08439C7.1451 9.81864 7.39236 8.39036 7.80904 7.06937H11.2869V11.2871ZM11.2869 5.64353H8.34313C8.58093 5.10109 8.84994 4.58415 9.14924 4.0988C9.89545 2.88858 10.693 2.10779 11.287 1.71924V5.64353H11.2869ZM21.352 7.06937C22.0375 8.36561 22.4448 9.79628 22.5434 11.2871H18.3421C18.2863 9.83064 18.0603 8.4063 17.6783 7.06937H21.352ZM19.4724 4.52752C19.8247 4.87979 20.1484 5.25296 20.4437 5.64357H17.1983C16.8809 4.82996 16.5019 4.06046 16.0641 3.35049C15.7209 2.79385 15.3522 2.29154 14.9728 1.8548C16.6558 2.3454 18.1982 3.25341 19.4724 4.52752ZM12.7128 1.71924C13.3067 2.10783 14.1043 2.88858 14.8505 4.0988C15.1498 4.58415 15.4188 5.10109 15.6566 5.64353H12.7128V1.71924H12.7128ZM12.7128 7.06937H16.1907C16.6074 8.39041 16.8546 9.81874 16.9153 11.2871H12.7128V7.06937ZM16.9153 12.7129C16.8546 14.1814 16.6073 15.6096 16.1906 16.9306H12.7127V12.7129H16.9153ZM12.7128 22.2808V18.3565H15.6566C15.4188 18.8989 15.1498 19.4159 14.8505 19.9012C14.1043 21.1115 13.3067 21.8922 12.7128 22.2808ZM19.4724 19.4725C18.1982 20.7466 16.6559 21.6546 14.9728 22.1452C15.3521 21.7085 15.7209 21.2062 16.0641 20.6496C16.5019 19.9396 16.8809 19.1701 17.1983 18.3565H20.4437C20.1484 18.7471 19.8247 19.1203 19.4724 19.4725ZM21.352 16.9306H17.6783C18.0603 15.5937 18.2863 14.1694 18.3421 12.7129H22.5434C22.4447 14.2037 22.0374 15.6344 21.352 16.9306Z"
              fill="#F15A22"
            />
          </g>
          <defs>
            <clipPath id="clip0_5_107">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
      {hideAllFilters ? null : (
        <button onClick={handleToggleFilter} className="h-9">
          <img src="/icons/page/filter.svg" />
        </button>
      )}
      {createPortal(
        <div className={cn(showFilter ? "" : "hidden")}>
          <div className="modalBackground" onClick={handleToggleFilter} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-md py-8 px-12 rounded-xl w-96 z-50 bg-modal flex flex-col gap-4">
            {user?.role === "admin" && (
              <div className="flex flex-col gap-4">
                <ProviderFilter />
                {hideTimeFilter ? null : (
                  <div className="w-full h-[1px] bg-secondary rounded-full" />
                )}
              </div>
            )}
            {hideTimeFilter ? null : <DateFilter />}
            {children !== undefined && (
              <div className="w-full h-[1px] bg-secondary rounded-full" />
            )}
            {children}
          </div>
        </div>,
        document.body
      )}
      {createPortal(
        <div className={cn(showLanguageSelector ? "" : "hidden")}>
          <div
            className="modalBackground"
            onClick={handleToggleLanguageSelector}
          />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-md py-8 px-12 rounded-xl w-72 z-50 bg-modal flex flex-col gap-4">
            <p>{t("filters.language_select")}</p>
            <div className="flex flex-col gap-1">
              {languages.map((language) => (
                <button
                  key={language.value}
                  onClick={handleChangeLanguage(language.value)}
                  className={cn(
                    "cursor-pointer hover:bg-secondary/20 transition-colors rounded py-1",
                    i18n.language === language.value ? "bg-secondary/20" : ""
                  )}
                >
                  {language.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
